import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
  ListGroup,
} from "react-bootstrap";
import { getHodDashboardDataApi } from "../../../services/DashboardServices";
import {
  FiUsers,
  FiBox,
  FiAlertTriangle,
  FiClock,
  FiActivity,
  FiMapPin,
  FiUser ,
} from "react-icons/fi";
import Chart from "react-apexcharts";

const statusColors = {
  IN_STOCK: "#6c757d",
  ALLOCATED: "#28a745",
  IN_REPAIR: "#ffc107",
  DISPOSED: "#dc3545",
};

const HodDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    getHodDashboardDataApi()
      .then((res) => {
        setData(res.data);
        setError("");
      })
      .catch((err) => {
        setError(
          err?.response?.data?.error ||
            "Failed to load dashboard data. Please try again."
        );
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <div>Loading HOD Dashboard...</div>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="my-4">
        {error}
      </Alert>
    );

  const d = data || {};

  // Status Breakdown Pie Chart Options
  const statusChartOptions = {
    chart: {
      type: "donut",
    },
    labels: d.status_breakdown?.map((item) => item.status) || [],
    colors: d.status_breakdown?.map((item) => statusColors[item.status]) || [],
    legend: {
      position: "right",
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return Math.round(val) + "%";
      },
    },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total Assets",
              formatter: function () {
                return d.total_assets || 0;
              },
            },
          },
        },
      },
    },
  };

  const statusChartSeries = d.status_breakdown?.map((item) => item.count) || [];

  // Assets by Area Bar Chart Options
  const areaChartOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: true,
      },
    },
    dataLabels: {
      enabled: false,
    },
    colors: ["#4e73df"],
    xaxis: {
      categories: d.assets_by_area?.map((item) => item.name) || [],
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val + " assets";
        },
      },
    },
  };

  const areaChartSeries = [
    {
      name: "Assets",
      data: d.assets_by_area?.map((item) => item.asset_count) || [],
    },
  ];

  return (
    <div>
      <Row className="mb-4 g-3">
        <Col md={3}>
          <Card className="text-center h-100 shadow-sm rounded">
            <Card.Body>
              <FiBox size={28} className="mb-2 text-primary" />
              <h5 className="fw-bold">Total Assets</h5>
              <div className="fs-3 fw-bold">{d.total_assets}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100 shadow-sm rounded">
            <Card.Body>
              <FiUsers size={28} className="mb-2 text-success" />
              <h5 className="fw-bold">Employees</h5>
              <div className="fs-3 fw-bold">{d.employee_count}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100 shadow-sm rounded">
            <Card.Body>
              <FiAlertTriangle size={28} className="mb-2 text-warning" />
              <h5 className="fw-bold">Expiring Warranty</h5>
              <div className="fs-3 fw-bold">
                {d.expiring_warranty?.length || 0}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100 shadow-sm rounded">
            <Card.Body>
              <FiClock size={28} className="mb-2 text-info" />
              <h5 className="fw-bold">Expiring Maintenance</h5>
              <div className="fs-3 fw-bold">
                {d.expiring_maintenance?.length || 0}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4 g-3">
        <Col md={6}>
          <Card className="h-100 shadow-sm rounded">
            <Card.Header className="d-flex align-items-center">
              <FiActivity className="me-2" /> Status Breakdown
            </Card.Header>
            <Card.Body>
              {d.status_breakdown?.length > 0 ? (
                <Chart
                  options={statusChartOptions}
                  series={statusChartSeries}
                  type="donut"
                  height={250}
                />
              ) : (
                <div className="">No status data available</div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100 shadow-sm rounded">
            <Card.Header className="d-flex align-items-center">
              <FiMapPin className="me-2" /> Assets by Area
            </Card.Header>
            <Card.Body>
              {d.assets_by_area?.length > 0 ? (
                <Chart
                  options={areaChartOptions}
                  series={areaChartSeries}
                  type="bar"
                  height={250}
                />
              ) : (
                <div className="">No area data available</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4 g-3">
        <Col md={6}>
          <Card className="h-100 shadow-sm rounded">
            <Card.Header className="d-flex align-items-center">
              <FiActivity className="me-2" /> Recent Asset Logs
            </Card.Header>
            <Card.Body className="p-3">
              <div className="thin-scrollbar" style={{ maxHeight: "200px", overflowY: "auto" }}>
                {d.recent_logs?.length > 0 ? (
                  <ListGroup variant="flush">
                    {d.recent_logs.map((log) => (
                      <ListGroup.Item key={log.id} className="py-2 px-0 hover-effect">
                        <div className="d-flex justify-content-between">
                          <div>
                            <strong>{log.asset__name}</strong>
                            <div className="small">
                              {log.asset__asset_number}
                            </div>
                          </div>
                          <Badge bg="light" text="dark">
                            {log.log_type}
                          </Badge>
                        </div>
                        <div className="small mt-1">
                          <span>
                            {log.created_at
                              ? new Date(log.created_at).toLocaleString()
                              : ""}
                          </span>
                          <span className="ms-2">
                            by <strong>{log.performed_by__fullname}</strong>
                          </span>
                        </div>
                        {log.remarks && (
                          <div className="small mt-1">{log.remarks}</div>
                        )}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <div>No recent logs</div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="h-100 shadow-sm rounded">
            <Card.Header className="d-flex align-items-center">
              <FiUser  className="me-2" /> Employees in Department
            </Card.Header>
            <Card.Body className="p-3">
              <div className="thin-scrollbar" style={{ maxHeight: "200px", overflowY: "auto" }}>
                {d.employees?.length > 0 ? (
                  <ListGroup variant="flush">
                    {d.employees.map((emp) => (
                      <ListGroup.Item key={emp.id} className="py-2 px-0 hover-effect">
                        <div className="d-flex align-items-center">
                          <div
                            className="me-3 bg-light rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: "40px", height: "40px" }}
                          >
                            <FiUser  size={18} />
                          </div>
                          <div>
                            <strong>{emp.fullname}</strong>
                            <div className="small">{emp.email}</div>
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <div>No employees found</div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <style>
        {`
          .thin-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
          }
          .thin-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .thin-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .thin-scrollbar::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.3);
            border-radius: 10px;
          }
          .thin-scrollbar::-webkit-scrollbar-thumb:hover {
            background-color: rgba(0, 0, 0, 0.5);
          }
          .hover-effect {
            transition: background-color 0.3s;
          }
          .hover-effect:hover {
            background-color: rgba(0, 0, 0, 0.05);
          }
        `}
      </style>
    </div>
  );
};

export default HodDashboard;
